// Firebase Configuration for Phoenix Hotels
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

// VAPID Key for Web Push Notifications
const vapidKey = import.meta.env.VITE_FCM_VAPID_KEY;

import { initializeApp } from "firebase/app";

// Firebase messaging is imported dynamically to avoid crashes on browsers
// that don't support the required APIs (e.g. iOS Safari in-browser).
// The module-level import of firebase/messaging can throw during evaluation
// on browsers without ServiceWorker or PushManager support.
let getMessagingFn = null;
let getTokenFn = null;
let onMessageFn = null;

const loadMessagingModule = async () => {
  if (getMessagingFn) return true;
  try {
    const mod = await import("firebase/messaging");
    getMessagingFn = mod.getMessaging;
    getTokenFn = mod.getToken;
    onMessageFn = mod.onMessage;
    return true;
  } catch (e) {
    console.warn("[Firebase] Could not load messaging module:", e.message);
    return false;
  }
};

// Try to load eagerly (non-blocking)
loadMessagingModule();

// Initialize Firebase App
let app;
let messaging;

try {
  if (
    !firebaseConfig.apiKey ||
    !firebaseConfig.authDomain ||
    !firebaseConfig.projectId
  ) {
    throw new Error(
      "Missing required Firebase configuration in environment variables",
    );
  }

  if (!vapidKey) {
    throw new Error("Missing VAPID key in environment variables");
  }

  app = initializeApp(firebaseConfig);

  // Try to initialize messaging eagerly if the module loaded synchronously.
  try {
    if (getMessagingFn) {
      messaging = getMessagingFn(app);
      console.log("[Firebase] Messaging initialized eagerly");
    }
  } catch (e) {
    console.warn("[Firebase] Eager messaging init failed, will retry lazily:", e.message);
  }
} catch (error) {
  console.error("Error initializing Firebase:", error);
}

/**
 * Lazily initialize Firebase Messaging if not already done.
 */
const getOrInitMessaging = async () => {
  if (messaging) return messaging;
  try {
    const loaded = await loadMessagingModule();
    if (loaded && app && getMessagingFn) {
      messaging = getMessagingFn(app);
      console.log("[Firebase] Messaging initialized lazily");
      return messaging;
    }
  } catch (e) {
    // iOS Safari can throw NotFoundError when IndexedDB is cleared while idle
    const isIOSIdleError = e.name === 'NotFoundError' || 
                           e.message?.includes('object can not be found') ||
                           e.message?.includes('IDBDatabase');
    if (isIOSIdleError) {
      console.warn("[Firebase] iOS idle state detected, messaging unavailable:", e.message);
    } else {
      console.error("[Firebase] Lazy messaging init failed:", e.message || e);
    }
  }
  return null;
};

/**
 * Get or register the Firebase messaging service worker.
 * On mobile browsers, getToken() MUST receive the SW registration explicitly
 * or it silently fails to subscribe to push.
 * @returns {Promise<ServiceWorkerRegistration|undefined>}
 */
const getServiceWorkerRegistration = async () => {
  if (!("serviceWorker" in navigator)) return undefined;

  try {
    // Check if our SW is already registered
    let existingReg;
    try {
      existingReg = await navigator.serviceWorker.getRegistration(
        "/firebase-messaging-sw.js",
      );
    } catch (e) {
      // iOS Safari can throw NotFoundError if SW was cleared while idle
      console.warn("[Firebase] SW getRegistration failed (iOS idle?):", e.message);
      existingReg = null;
    }
    
    if (existingReg) {
      // Make sure it's activated
      if (existingReg.active) {
        return existingReg;
      }
      // Wait for it to activate
      await navigator.serviceWorker.ready;
      return existingReg;
    }

    // Register fresh
    const registration = await navigator.serviceWorker.register(
      "/firebase-messaging-sw.js",
      { scope: "/" },
    );

    // Wait for the SW to be ready
    await navigator.serviceWorker.ready;
    return registration;
  } catch (error) {
    // Don't throw — just return undefined and let caller handle gracefully
    console.warn("[Firebase] Service worker registration failed:", error.message);
    return undefined;
  }
};

/**
 * Request notification permission and get FCM token
 * @returns {Promise<string|null>} FCM token or null if permission denied
 */
export const requestNotificationPermission = async () => {
  try {
    if (!("Notification" in window)) {
      console.warn("This browser does not support notifications");
      return null;
    }

    if (Notification.permission === "denied") {
      console.warn("Notification permission was previously denied");
      return null;
    }

    // Request permission if not already granted
    if (Notification.permission !== "granted") {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        return null;
      }
    }

    return await getFCMToken();
  } catch (error) {
    console.error("Error requesting notification permission:", error);
    return null;
  }
};

/**
 * Get FCM registration token.
 * Passes the service worker registration explicitly — this is critical for mobile browsers.
 * @returns {Promise<string|null>} FCM token or null if failed
 */
export const getFCMToken = async () => {
  try {
    const msg = await getOrInitMessaging();
    if (!msg || !getTokenFn) {
      console.warn("Firebase messaging not available");
      return null;
    }

    // Get the service worker registration — required for mobile browsers
    const swRegistration = await getServiceWorkerRegistration();

    const tokenOptions = {
      vapidKey: vapidKey,
    };

    // Pass SW registration explicitly (critical for mobile)
    if (swRegistration) {
      tokenOptions.serviceWorkerRegistration = swRegistration;
    }

    const currentToken = await getTokenFn(msg, tokenOptions);

    if (currentToken) {
      console.log("[Firebase] FCM token obtained successfully");
      return currentToken;
    } else {
      console.warn("[Firebase] No registration token available");
      return null;
    }
  } catch (error) {
    if (
      error.code === "messaging/permission-blocked" ||
      error.code === "messaging/notifications-blocked"
    ) {
      console.warn("[Firebase] Notifications blocked by user");
    } else if (error.message?.includes("push service")) {
      console.warn(
        "[Firebase] Push service error (common on iOS in-browser):",
        error.message,
      );
    } else {
      console.error("[Firebase] Error retrieving FCM token:", error);
    }
    return null;
  }
};

/**
 * Listen for foreground messages.
 * Returns an unsubscribe function. If messaging isn't available yet,
 * sets up the listener asynchronously and returns a cleanup function.
 */
export const onForegroundMessage = (callback) => {
  // If messaging is already initialized, subscribe synchronously
  if (messaging && onMessageFn) {
    return onMessageFn(messaging, (payload) => {
      callback(payload);
    });
  }

  // Otherwise, try to init async and subscribe when ready
  let unsubscribe = null;
  let cancelled = false;

  getOrInitMessaging().then((msg) => {
    if (cancelled || !msg || !onMessageFn) return;
    unsubscribe = onMessageFn(msg, (payload) => {
      callback(payload);
    });
  }).catch(() => {});

  // Return a cleanup function that cancels the pending setup or unsubscribes
  return () => {
    cancelled = true;
    if (unsubscribe && typeof unsubscribe === "function") {
      unsubscribe();
    }
  };
};

/**
 * Check if notifications are supported in this browser/context.
 * We keep this very permissive — just check for the Notification API.
 * Actual push capability is verified when the user tries to enable.
 * @returns {boolean}
 */
export const isNotificationSupported = () => {
  return "Notification" in window;
};

/**
 * Detect if running on iOS (iPhone/iPad, NOT macOS Safari)
 */
export const isIOS = () => {
  const ua = navigator.userAgent;
  // Match actual iOS devices via userAgent
  if (/iPad|iPhone|iPod/.test(ua)) {
    return true;
  }
  // iPadOS 13+ spoofs macOS UA — detect via touch + no fine pointer (mouse/trackpad)
  if (
    navigator.platform === "MacIntel" &&
    navigator.maxTouchPoints > 1 &&
    !window.matchMedia("(pointer: fine)").matches
  ) {
    return true;
  }
  return false;
};

/**
 * Detect if running as installed PWA (Add to Home Screen)
 */
export const isInstalledPWA = () => {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
};

/**
 * Check if push notifications can work in the current context.
 * This is a soft hint — the real test happens when we actually try to get a token.
 * Only returns false for known-impossible cases (iOS Safari not in PWA mode).
 */
export const canReceivePush = () => {
  if (!("Notification" in window)) return false;
  // iOS Safari requires PWA mode for push
  if (isIOS() && !isInstalledPWA()) return false;
  return true;
};

/**
 * Get current notification permission status
 * @returns {NotificationPermission}
 */
export const getNotificationPermission = () => {
  if (!("Notification" in window)) {
    return "denied";
  }
  return Notification.permission;
};

/**
 * Generate a unique device ID
 * @returns {string}
 */
export const getDeviceId = () => {
  let deviceId = localStorage.getItem("deviceId");

  if (!deviceId) {
    deviceId = `web-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem("deviceId", deviceId);
  }

  return deviceId;
};

/**
 * Get device information
 * @returns {Object}
 */
export const getDeviceInfo = () => {
  const ua = navigator.userAgent;
  let platform = "web";
  if (/Android/i.test(ua)) platform = "android-web";
  else if (/iPad|iPhone|iPod/i.test(ua)) platform = "ios-web";

  return {
    model: ua,
    osVersion: navigator.platform,
    appBuild: import.meta.env.VITE_APP_VERSION || "1.0.0",
    mobilePlatform: platform,
  };
};

/**
 * Show a notification via the service worker (works on mobile).
 * Falls back to `new Notification()` on desktop if SW is unavailable.
 */
export const showNotificationViaServiceWorker = async (title, options = {}) => {
  try {
    const registration = await navigator.serviceWorker?.getRegistration("/");
    if (registration) {
      await registration.showNotification(title, options);
      return true;
    }
  } catch (e) {
    console.warn("[Firebase] SW showNotification failed, trying fallback:", e);
  }

  // Fallback for desktop browsers where SW might not be ready
  try {
    if ("Notification" in window && Notification.permission === "granted") {
      const n = new Notification(title, options);
      setTimeout(() => n.close(), options.requireInteraction ? 10000 : 5000);
      return true;
    }
  } catch (e) {
    console.warn("[Firebase] Fallback Notification() also failed:", e);
  }

  return false;
};

export { messaging, firebaseConfig, vapidKey };
